// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Projects summary card component.
 * Shows per-status project counts with contract-value totals broken out by
 * currency, grouped client-side from the customer's projects.
 */
import { CardHeader } from '@/components/card-header';
import { ProjectStatus } from '@/constants/project-status';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { Project } from '@/project/api/types';
import { ProjectStatusChip } from '@/project/components/project-status-chip';
import { summarizeProjects } from '@/project/utilities/projects-summary';
import { formatNumber } from '@/utilities/number';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { Decimal } from 'decimal.js';
import { useMemo } from 'react';

interface Props {
  projects: Project[];
}

// Status display order, matching the project list and filter ordering.
const STATUS_ORDER: ProjectStatus[] = [
  'ACTIVE',
  'UPCOMING',
  'PAST',
  'DATE_RANGE_INCOMPLETE',
];

export function ProjectsSummaryCard(props: Props) {
  const { countByStatus, totalsByStatus, currencies } = useMemo(
    () => summarizeProjects(props.projects),
    [props.projects]
  );

  return (
    <Paper variant="outlined">
      <CardHeader title="Projects summary" />
      <Divider />
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          sm: '1fr 1fr',
          md: '1fr 1fr 1fr 1fr',
        }}
        gap={2}
        padding={2}
      >
        {STATUS_ORDER.map((status) => {
          const count = countByStatus.get(status) ?? 0;
          const statusTotals = totalsByStatus.get(status);

          return (
            <Stack
              key={status}
              spacing={1}
              alignItems="center"
              justifyContent="center"
              padding={2}
            >
              <ProjectStatusChip status={status} />
              <Typography variant="h3">{formatNumber(count)}</Typography>
              <Stack spacing={0.5} alignItems="center">
                {currencies.map((currencyCode) => (
                  <Typography
                    key={currencyCode}
                    variant="body1"
                    color="text.secondary"
                  >
                    {formatCurrencyValue({
                      isoCode: currencyCode,
                      amount: statusTotals?.get(currencyCode) ?? new Decimal(0),
                    })}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          );
        })}
      </Box>
    </Paper>
  );
}
